import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { COUNTRIES, CountryTabs } from '../components/country-tabs';
import { ApiError, api } from '../config/api';
import { useConnections, useDisconnect } from '../hooks/use-connections';

function validCountryCode(raw: string | null): string {
  if (!raw) return 'global';
  const upper = raw.toUpperCase();
  const found = COUNTRIES.find(
    (c) => c.code.toUpperCase() === upper || c.code === raw,
  );
  return found?.code ?? 'global';
}

function countryLabel(code: string): string {
  return COUNTRIES.find((c) => c.code === code)?.label ?? code;
}

/**
 * Lists stored social connections per country tab.
 *
 * The country tab bar mirrors the Composer: URL `?country=SE` drives state
 * so refresh / share / bookmark holds the selection. "Connect Instagram"
 * targets the active tab — the popup hits `/oauth/instagram/start?country=SE`,
 * the backend stores `country=SE` on upsert, and on success the popup
 * postMessages back so this page refetches and reveals the new row.
 *
 * The `global` tab is the brand-level account (country IS NULL in DB) —
 * the OAuth start URL omits the param in that case.
 */
export function ConnectionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCountry = validCountryCode(searchParams.get('country'));

  // `global` tab → /connections?country=global (server maps to NULL).
  // Any other tab → /connections?country=SE etc.
  const countryFilter = activeCountry === 'global' ? 'global' : activeCountry;

  const { data, isLoading, error, refetch } = useConnections(countryFilter);
  const disconnect = useDisconnect();
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      // The OAuth callback HTML postMessages a small JSON payload right
      // before closing. Refetch the list to reflect newly-stored connections.
      if (typeof ev.data === 'object' && ev.data && 'ok' in ev.data) {
        void refetch();
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [refetch]);

  function onCountryChange(code: string) {
    const next = new URLSearchParams(searchParams);
    if (code === 'global') next.delete('country');
    else next.set('country', code);
    setSearchParams(next, { replace: true });
  }

  /**
   * Kick off Instagram OAuth.
   *
   * The Meta Login URL is fetched via an authenticated XHR (the Bearer JWT
   * rides in the `Authorization` header). The popup then opens the returned
   * URL, which points at facebook.com — the popup itself never has to reach
   * our admin-guarded backend, so there is no need for it to carry a JWT.
   *
   * The earlier "popup hits /oauth/instagram/start directly" flow died on
   * a 401: a top-level window.open cannot send `Authorization` headers,
   * only cookies, and the social-generator API uses Bearer tokens.
   */
  async function connectInstagram() {
    setConnectError(null);
    setConnecting(true);
    try {
      const params = new URLSearchParams();
      if (activeCountry !== 'global') params.set('country', activeCountry);
      const qs = params.toString();
      const path =
        `/oauth/instagram/authorize-url` + (qs ? `?${qs}` : '');
      const { url } = await api<{ url: string }>(path);
      window.open(
        url,
        'newswave-oauth',
        'width=600,height=720,menubar=no,toolbar=no',
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setConnectError(`${err.message} — ${JSON.stringify(err.body)}`);
      } else if (err instanceof Error) {
        setConnectError(err.message);
      } else {
        setConnectError('Failed to start Instagram OAuth');
      }
    } finally {
      setConnecting(false);
    }
  }

  const label = countryLabel(activeCountry);
  const connectLabel =
    activeCountry === 'global'
      ? 'Connect Instagram for Global brand'
      : `Connect Instagram for ${label}`;

  return (
    <div className="page">
      <header className="page-head">
        <Link to="/" className="link">
          ← Composer
        </Link>
        <h1>Connections</h1>
      </header>

      <section className="panel panel-tabs">
        <div className="panel-label">Country</div>
        <CountryTabs value={activeCountry} onChange={onCountryChange} />
      </section>

      <div className="connections-actions">
        <button
          className="btn-primary"
          onClick={connectInstagram}
          disabled={connecting}
        >
          {connecting ? 'Opening Meta…' : connectLabel}
        </button>
        <span className="dim small">
          Facebook · Threads · LinkedIn · X — coming soon
        </span>
      </div>

      {connectError ? <div className="error">{connectError}</div> : null}

      {isLoading ? <div className="muted">Loading…</div> : null}
      {error ? <div className="error">{error.message}</div> : null}
      {data ? (
        <table className="runs-table">
          <thead>
            <tr>
              <th>Platform</th>
              <th>Account</th>
              <th>Scopes</th>
              <th>Connected</th>
              <th>Expires</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.map((c) => (
              <tr key={c.id}>
                <td>
                  <span className="pill pill-rendered">{c.platform}</span>
                </td>
                <td>{c.accountHandle}</td>
                <td className="dim small">{c.scopes.join(', ')}</td>
                <td className="dim mono">
                  {new Date(c.createdAt).toLocaleString()}
                </td>
                <td className="dim mono">
                  {c.expiresAt
                    ? new Date(c.expiresAt).toLocaleDateString()
                    : '—'}
                </td>
                <td>
                  <button
                    className="btn-link"
                    onClick={() => disconnect.mutate(c.id)}
                  >
                    Disconnect
                  </button>
                </td>
              </tr>
            ))}
            {data.length === 0 ? (
              <tr>
                <td colSpan={6} className="muted">
                  No {label} connection yet. Click "{connectLabel}" to link an
                  account.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
